import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from '@/components/ThemeProvider';
import Login from './pages/Login';
import SuperAdminLogin from './pages/SuperAdminLogin';
import TherapistDashboard from './pages/therapist/Dashboard';
import TherapistAttendance from './pages/therapist/Attendance';
import TherapistServiceEntry from './pages/therapist/ServiceEntry';
import TherapistPerformance from './pages/therapist/Performance';
import TherapistOperations from './pages/therapist/TherapistOperations';
import AdminDashboard from './pages/admin/Dashboard';
import AdminProperties from './pages/admin/Properties';
import AdminTherapists from './pages/admin/Therapists';
import AdminServices from './pages/admin/Services';
import AdminReports from './pages/admin/Reports';
import AdminIncentives from './pages/admin/Incentives';
import AdminExpenses from './pages/admin/Expenses';
import AdminSettings from './pages/admin/Settings';
import AdminAttendance from './pages/admin/Attendance';
import AdminCustomers from './pages/admin/Customers';
import AdminAnalytics from './pages/admin/Analytics';
import OperationsDashboard from './pages/admin/OperationsDashboard';
import LeaveManagement from './pages/admin/LeaveManagement';
import FeedbackDashboard from './pages/admin/FeedbackDashboard';
import FeedbackForm from './pages/FeedbackForm';
import EnhancedReports from './pages/admin/EnhancedReports';
import HotelPortal from './pages/hotel/HotelPortal';
import HotelUserManagement from './pages/admin/HotelUserManagement';
import CustomerIntelligenceDashboard from './pages/admin/CustomerIntelligenceDashboard';
import PaymentCorrectionSystem from './pages/admin/PaymentCorrectionSystem';
import SMSSettings from './pages/admin/SMSSettings';
import WhatsAppConfiguration from './pages/admin/WhatsAppConfiguration';
import EmailConfiguration from './pages/admin/EmailConfiguration';
import TherapistLoginManagement from './pages/admin/TherapistLoginManagement';
import ManageProperty from './pages/admin/ManageProperty';
import CustomerFeedback from './pages/public/CustomerFeedback';
import SubscriptionGate from './components/SubscriptionGate';
// Wellness Technologies SaaS Platform Routes
import WellnessTechLanding from './pages/public/WellnessTechLanding';
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard';
import TenantOnboarding from './pages/onboarding/TenantOnboarding';
import PaymentPage from './pages/onboarding/PaymentPage';
import OnboardingSuccess from './pages/onboarding/OnboardingSuccess';
import CommunicationSettings from './pages/admin/CommunicationSettings';
import SubscriptionPage from './pages/admin/SubscriptionPage';
import MyPlanPage from './pages/admin/MyPlanPage';
// Policy & Documentation Pages
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import TermsOfService from './pages/public/TermsOfService';
import RefundPolicy from './pages/public/RefundPolicy';
import APIDocumentation from './pages/public/APIDocumentation';
import Integrations from './pages/public/Integrations';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Determine redirect path based on user role
  const getRedirectPath = (user) => {
    if (!user) return '/login';
    switch (user.role) {
      case 'platform_super_admin':
        return '/platform';
      case 'super_admin':
        return '/admin';
      case 'hotel':
        return '/hotel';
      case 'therapist':
      case 'therapist_shared':
        return '/therapist';
      default:
        return '/login';
    }
  };

  if (loading) {
    return (
      <ThemeProvider>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-muted-foreground">Loading...</div>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <div className="App min-h-screen bg-background">
        <BrowserRouter>
          <Routes>
            {/* ========== PUBLIC / MARKETING ROUTES ========== */}
            <Route path="/welcome" element={<WellnessTechLanding onLogin={handleLogin} />} />
            <Route path="/feedback/:token" element={<FeedbackForm />} />
            <Route path="/customer-feedback/:token" element={<CustomerFeedback />} />
            
            {/* ========== SUPER ADMIN LOGIN (HIDDEN) ========== */}
            <Route path="/wtadmin" element={
              user?.role === 'platform_super_admin' 
                ? <Navigate to="/platform" /> 
                : <SuperAdminLogin onLogin={handleLogin} />
            } />
            <Route path="/platform-admin" element={
              user?.role === 'platform_super_admin' 
                ? <Navigate to="/platform" /> 
                : <SuperAdminLogin onLogin={handleLogin} />
            } />
            
            {/* ========== POLICY & DOCUMENTATION ROUTES ========== */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/api-documentation" element={<APIDocumentation />} />
            <Route path="/integrations" element={<Integrations />} />
            
            {/* ========== TENANT ONBOARDING ROUTES ========== */}
            <Route path="/onboarding" element={<TenantOnboarding />} />
            <Route path="/onboarding/payment" element={<PaymentPage />} />
            <Route path="/onboarding/success" element={<OnboardingSuccess />} />
            
            {/* ========== PLATFORM SUPER ADMIN ROUTES ========== */}
            <Route
              path="/platform"
              element={user?.role === 'platform_super_admin' ? 
                <SuperAdminDashboard user={user} onLogout={handleLogout} /> : 
                <Navigate to="/wtadmin" />}
            />
            
            {/* ========== AUTH ROUTES ========== */}
            {/* Tenant login - always shows the login form, even for logged-in super admins */}
            {/* Super admins should access /wtadmin, not /login */}
            <Route
              path="/login"
              element={
                !user || user.role === 'platform_super_admin' 
                  ? <Login onLogin={handleLogin} /> 
                  : <Navigate to={getRedirectPath(user)} />
              }
            />
          
          {/* Hotel Routes - Wrapped with SubscriptionGate for force logout */}
          <Route
            path="/hotel"
            element={user?.role === 'hotel' ? 
              <SubscriptionGate user={user} onLogout={handleLogout}>
                <HotelPortal user={user} onLogout={handleLogout} />
              </SubscriptionGate> : <Navigate to="/login" />}
          />
          
          {/* Therapist Routes - support both therapist and therapist_shared */}
          <Route
            path="/therapist"
            element={['therapist', 'therapist_shared'].includes(user?.role) ? 
              <SubscriptionGate user={user} onLogout={handleLogout}>
                <TherapistDashboard user={user} onLogout={handleLogout} />
              </SubscriptionGate> : <Navigate to="/login" />}
          />
          <Route
            path="/therapist/attendance"
            element={['therapist', 'therapist_shared'].includes(user?.role) ? 
              <SubscriptionGate user={user} onLogout={handleLogout}>
                <TherapistAttendance user={user} onLogout={handleLogout} />
              </SubscriptionGate> : <Navigate to="/login" />}
          />
          <Route
            path="/therapist/service-entry"
            element={['therapist', 'therapist_shared'].includes(user?.role) ? 
              <SubscriptionGate user={user} onLogout={handleLogout}>
                <TherapistServiceEntry user={user} onLogout={handleLogout} />
              </SubscriptionGate> : <Navigate to="/login" />}
          />
          <Route
            path="/therapist/performance"
            element={['therapist', 'therapist_shared'].includes(user?.role) ? 
              <SubscriptionGate user={user} onLogout={handleLogout}>
                <TherapistPerformance user={user} onLogout={handleLogout} />
              </SubscriptionGate> : <Navigate to="/login" />}
          />
          
          {/* Admin Routes - Wrapped with SubscriptionGate */}
          <Route
            path="/admin"
            element={user?.role === 'super_admin' ? 
              <SubscriptionGate user={user} onLogout={handleLogout}>
                <AdminDashboard user={user} onLogout={handleLogout} />
              </SubscriptionGate> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/my-plan"
            element={user?.role === 'super_admin' ? <MyPlanPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/properties"
            element={user?.role === 'super_admin' ? 
              <SubscriptionGate user={user} onLogout={handleLogout}>
                <AdminProperties user={user} onLogout={handleLogout} />
              </SubscriptionGate> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/therapists"
            element={user?.role === 'super_admin' ? <AdminTherapists user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/services"
            element={user?.role === 'super_admin' ? <AdminServices user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/reports"
            element={user?.role === 'super_admin' ? <AdminReports user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/incentives"
            element={user?.role === 'super_admin' ? <AdminIncentives user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/expenses"
            element={user?.role === 'super_admin' ? <AdminExpenses user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/settings"
            element={user?.role === 'super_admin' ? <AdminSettings user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/attendance"
            element={user?.role === 'super_admin' ? <AdminAttendance user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/customers"
            element={user?.role === 'super_admin' ? <AdminCustomers user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/analytics"
            element={user?.role === 'super_admin' ? <AdminAnalytics user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/intelligence"
            element={user?.role === 'super_admin' ? <CustomerIntelligenceDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/operations"
            element={user?.role === 'super_admin' ? <OperationsDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/communication"
            element={user?.role === 'super_admin' ? <CommunicationSettings user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/subscription"
            element={user?.role === 'super_admin' ? <SubscriptionPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/subscribe"
            element={user?.role === 'super_admin' ? <SubscriptionPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          {/* Therapist can also access operations */}
          <Route
            path="/therapist/operations"
            element={['therapist', 'therapist_shared'].includes(user?.role) ? <TherapistOperations user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          
          {/* Leave Management - Admin and Therapist */}
          <Route
            path="/admin/leave"
            element={user?.role === 'super_admin' ? <LeaveManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/therapist/leave"
            element={['therapist', 'therapist_shared'].includes(user?.role) ? <LeaveManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          
          {/* Feedback Dashboard - Admin only */}
          <Route
            path="/admin/feedback"
            element={user?.role === 'super_admin' ? <FeedbackDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          
          {/* Enhanced Reports - Admin only */}
          <Route
            path="/admin/enhanced-reports"
            element={user?.role === 'super_admin' ? <EnhancedReports user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          
          {/* Hotel User Management - Admin only */}
          <Route
            path="/admin/hotel-users"
            element={user?.role === 'super_admin' ? <HotelUserManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          
          {/* Payment Correction System - Admin only */}
          <Route
            path="/admin/payment-corrections"
            element={user?.role === 'super_admin' ? <PaymentCorrectionSystem user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          
          {/* SMS Settings - Admin only (legacy) */}
          <Route
            path="/admin/sms-settings"
            element={user?.role === 'super_admin' ? <SMSSettings user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          
          {/* WhatsApp Configuration - Admin only */}
          <Route
            path="/admin/whatsapp"
            element={user?.role === 'super_admin' ? <WhatsAppConfiguration user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          
          {/* Email Configuration - Admin only */}
          <Route
            path="/admin/email"
            element={user?.role === 'super_admin' ? <EmailConfiguration user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          
          {/* Therapist Login Management - Admin only */}
          <Route
            path="/admin/therapist-logins"
            element={user?.role === 'super_admin' ? <TherapistLoginManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          
          {/* Manage Property - Admin only */}
          <Route
            path="/admin/properties/:propertyId/manage"
            element={user?.role === 'super_admin' ? <ManageProperty user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          
          <Route path="/" element={<Navigate to="/welcome" />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
      </div>
    </ThemeProvider>
  );
}

export default App;